import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../axiosConfig';
import '../../styles/explorePageCss/bookDisplay.css';
import PDFCoverPreview from '../../components/PDFCoverPreview';

const CHIP_TONES = ['primary', 'mint', 'sky', 'lilac', 'peach'];

const FILTER_DEFS = [
  { key: 'subject', label: 'Subject', style: 'checkbox' },
  { key: 'grade', label: 'Grade', style: 'pills' },
  { key: 'bookType', label: 'Book Type', style: 'checkbox' },
  { key: 'version', label: 'Version', style: 'checkbox' },
  { key: 'language', label: 'Language', style: 'checkbox' },
  { key: 'country', label: 'Country', style: 'checkbox' },
];

const BOOKS_PER_PAGE = 20;

function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items = [1];
  if (current > 3) items.push('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p += 1) {
    if (!items.includes(p)) items.push(p);
  }
  if (current < total - 2) items.push('…');
  if (!items.includes(total)) items.push(total);
  return items;
}

const Books = () => {
  const [books, setBooks] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const initialFilters = {
    subject: [],
    grade: [],
    bookType: [],
    version: [],
    language: [],
    country: [],
    standards: [],
    isbn: [],
  };
  const [filters, setFilters] = useState(initialFilters);
  const [openFilterSections, setOpenFilterSections] = useState(() =>
    Object.fromEntries(FILTER_DEFS.map(({ key }) => [key, true]))
  );
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeBlobUrl, setIframeBlobUrl] = useState(null);
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerFullscreen, setViewerFullscreen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const toggleFilterValue = (category, value) => {
    const next = String(value);
    setFilters((prev) => {
      const current = prev[category] || [];
      const exists = current.includes(next);
      return {
        ...prev,
        [category]: exists ? current.filter((v) => v !== next) : [...current, next],
      };
    });
  };

  const toggleFilterSection = (key) => {
    setOpenFilterSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getName = (list, id, key) =>
    list.find((item) => String(item[key]) === String(id))?.[`${key.replace('_id', '')}_name`] || '—';
  const getGradeName = (id) =>
    grades.find((g) => String(g.grade_id) === String(id))?.grade_level || '—';

  const unique = (key) =>
    [
      ...new Set(
        books
          .map((b) => {
            switch (key) {
              case 'bookType':
                return b.book_type_title;
              case 'version':
                return b.version_label;
              case 'country':
                return b.country_name;
              case 'isbn':
                return b.isbn_code;
              default:
                return b[key];
            }
          })
          .filter((v) => v)
          .map((v) => v.toString().trim())
      ),
    ];

  function groupBooksByLogicalBook(list) {
    const grouped = {};
    for (const book of list) {
      const key = [book.title, book.isbn_code, book.grade_id, book.version_label, book.book_id].join('|');
      if (!grouped[key]) {
        grouped[key] = {
          title: book.title,
          isbn_code: book.isbn_code,
          grade_id: book.grade_id,
          grade_name: book.grade_name,
          version_label: book.version_label,
          book_id: book.book_id,
          digital: null,
          print: null,
          description: book.description,
          subject_id: book.subject_id,
          subject_name: book.subject_name,
          language_id: book.language_id,
          language_name: book.language_name,
          book_type_title: book.book_type_title,
          country_name: book.country_name,
          tags: book.tags,
          url: book.url || null,
        };
      }
      const formatName = (book.format_name || '').toLowerCase();
      if (formatName.includes('digital')) {
        grouped[key].digital = book;
        if (book.url && !grouped[key].url) grouped[key].url = book.url;
      } else if (formatName.includes('print')) {
        grouped[key].print = book;
        if (book.url && !grouped[key].url) grouped[key].url = book.url;
      } else if (!grouped[key].digital) {
        grouped[key].digital = book;
        if (book.url && !grouped[key].url) grouped[key].url = book.url;
      }
    }
    return Object.values(grouped);
  }

  const filterOptions = (cat) => {
    if (cat === 'subject') {
      return subjects.map((o) => ({ value: String(o.subject_id), label: o.subject_name }));
    }
    if (cat === 'grade') {
      return grades.map((o) => ({ value: String(o.grade_id), label: o.grade_level }));
    }
    if (cat === 'language') {
      return languages.map((o) => ({ value: String(o.language_id), label: o.language_name }));
    }
    return unique(cat).map((v) => ({ value: String(v), label: v }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const [booksRes, gradesRes, subjectsRes, languagesRes, bookTypesRes] =
          await Promise.all([
            axios.get('/books', { headers }),
            axios.get('/grades', { headers }),
            axios.get('/subjects', { headers }),
            axios.get('/languages', { headers }),
            axios.get('/booktypes', { headers }),
          ]);

        const gradeMap = new Map(gradesRes.data.map((g) => [g.grade_id, g.grade_level]));
        const subjectMap = new Map(subjectsRes.data.map((s) => [s.subject_id, s.subject_name]));
        const languageMap = new Map(languagesRes.data.map((l) => [l.language_id, l.language_name]));
        const bookTypeMap = new Map(bookTypesRes.data.map((bt) => [bt.book_type_id, bt.book_type_title]));

        const booksWithExtras = booksRes.data.books.map((book) => ({
          ...book,
          grade_name: gradeMap.get(book.grade_id) || 'N/A',
          subject_name: subjectMap.get(book.subject_id) || 'N/A',
          language_name: languageMap.get(book.language_id) || 'N/A',
          book_type_title: bookTypeMap.get(book.booktype_id) || 'N/A',
          version_label: book.version_label || 'N/A',
          isbn_code: book.isbn_code || 'N/A',
          file_size: book.file_size || book.versions?.[0]?.file_size || null,
        }));

        const groupedBooks = groupBooksByLogicalBook(booksWithExtras);
        setBooks(groupedBooks);
        setFilteredBooks(groupedBooks);
        setGrades(gradesRes.data);
        setSubjects(subjectsRes.data);
        setLanguages(languagesRes.data);
      } catch (err) {
        console.error('Fetch error:', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const filtered = books.filter((group) => {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const searchMatch =
        !searchTerm ||
        (group.title?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.description?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.grade_name?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.subject_name?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.language_name?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.country_name?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        (group.isbn_code?.toLowerCase() || '').includes(lowerCaseSearchTerm) ||
        group.tags?.some((tag) => tag.tag_name.toLowerCase().includes(lowerCaseSearchTerm));

      if (!searchMatch) return false;

      const match = (list, val) => list.length === 0 || list.includes(String(val));
      return (
        match(filters.grade, group.grade_id) &&
        match(filters.subject, group.subject_id) &&
        match(filters.bookType, group.book_type_title) &&
        match(filters.version, group.version_label) &&
        match(filters.country, group.country_name) &&
        match(filters.language, group.language_id) &&
        match(filters.standards, group.standard_id) &&
        match(filters.isbn, group.isbn_code)
      );
    });
    setFilteredBooks(filtered);
    setCurrentPage(1);
  }, [filters, books, searchTerm]);

  const closeIframe = useCallback(() => {
    setShowIframe(false);
    setIframeUrl(null);
    setIframeLoading(false);
    setViewerTitle('');
    setViewerFullscreen(false);
    if (iframeBlobUrl) {
      URL.revokeObjectURL(iframeBlobUrl);
      setIframeBlobUrl(null);
    }
  }, [iframeBlobUrl]);

  const openViewer = (url, title) => {
    setViewerTitle(title || 'Book Reader');
    setViewerFullscreen(false);
    setIframeUrl(url);
    setShowIframe(true);
    setSelectedBook(null);
  };

  const resolveBookUrl = (book) => {
    if (!book) return null;
    const direct = book.url && String(book.url).trim();
    if (direct) return direct;
    const foundGroup = books.find((group) => {
      const main = group.digital || group.print;
      return main && main.book_id === book.book_id;
    });
    const fromGroup =
      foundGroup?.url || foundGroup?.digital?.url || foundGroup?.print?.url || null;
    return fromGroup && String(fromGroup).trim() ? String(fromGroup).trim() : null;
  };

  const handleViewBook = async () => {
    if (!selectedBook?.book_id) return;
    const title = selectedBook.title || 'Book Reader';

    const externalUrl = resolveBookUrl(selectedBook);
    if (externalUrl) {
      openViewer(externalUrl, title);
      return;
    }

    try {
      setIframeLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/books/${selectedBook.book_id}/stream-version`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });

      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        throw new Error('Book file not available');
      }

      if (iframeBlobUrl) URL.revokeObjectURL(iframeBlobUrl);
      const blob = new Blob([response.data], {
        type: contentType || 'application/pdf',
      });
      const objectUrl = URL.createObjectURL(blob);
      setIframeBlobUrl(objectUrl);
      openViewer(objectUrl, title);
    } catch (err) {
      console.error('Failed to open book:', err);
      alert('Unable to open this book. No uploaded PDF or URL is available.');
    } finally {
      setIframeLoading(false);
    }
  };

  const handleSelectBook = (book) => {
    setSelectedBook(book);
    setIsDescriptionExpanded(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('bookx-details-overlay')) {
      setSelectedBook(null);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape' || !showIframe) return;
      if (viewerFullscreen) {
        setViewerFullscreen(false);
        return;
      }
      closeIframe();
    };
    if (showIframe) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [showIframe, viewerFullscreen, closeIframe]);

  let truncatedDescription = '';
  let isLongDescription = false;
  if (selectedBook) {
    const wordLimit = 100;
    const fullDescription = selectedBook.description || 'No description available.';
    const words = fullDescription.split(/\s+/);
    isLongDescription = words.length > wordLimit;
    truncatedDescription =
      isLongDescription && !isDescriptionExpanded
        ? `${words.slice(0, wordLimit).join(' ')}...`
        : fullDescription;
  }

  const totalPages = Math.max(1, Math.ceil(filteredBooks.length / BOOKS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * BOOKS_PER_PAGE;
  const pagedBooks = filteredBooks.slice(pageStart, pageStart + BOOKS_PER_PAGE);
  const showingFrom = filteredBooks.length ? pageStart + 1 : 0;
  const showingTo = Math.min(pageStart + BOOKS_PER_PAGE, filteredBooks.length);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return (
    <div className="bookx-container">
      <div className="page-head">
        <div>
          <h1 className="page-title">Books Available</h1>
          <p className="page-desc">Browse the full shared catalogue the way a reader would.</p>
        </div>
      </div>

      <div className="layout-with-filters explore-books-layout">
        <aside>
          <div className="filter-card explore-filter-panel">
            <div className="explore-filter-head">
              <div className="explore-filter-title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <span>Filters</span>
              </div>
              <button type="button" className="explore-filter-clear" onClick={handleClearFilters}>
                Clear all
              </button>
            </div>

            <div className="explore-filter-body">
              {FILTER_DEFS.map(({ key, label, style }) => {
                const options = filterOptions(key);
                if (options.length === 0) return null;
                const selected = filters[key] || [];
                const isOpen = openFilterSections[key] !== false;
                const selectedCount = selected.length;

                return (
                  <section key={key} className={`explore-filter-section${isOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="explore-filter-section-toggle"
                      onClick={() => toggleFilterSection(key)}
                      aria-expanded={isOpen}
                    >
                      <span className="explore-filter-section-title">
                        {label}
                        {selectedCount > 0 ? (
                          <span className="explore-filter-count">{selectedCount}</span>
                        ) : null}
                      </span>
                      <svg
                        className="explore-filter-chevron"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        aria-hidden="true"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {isOpen ? (
                      style === 'pills' ? (
                        <div className={`explore-filter-pills${key === 'grade' ? ' is-scrollable' : ''}`}>
                          {options.map((opt) => {
                            const active = selected.includes(opt.value);
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                className={`explore-filter-pill ${active ? 'is-active' : ''}`}
                                onClick={() => toggleFilterValue(key, opt.value)}
                                title={opt.label}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <ul className="explore-filter-checks">
                          {options.map((opt) => {
                            const checked = selected.includes(opt.value);
                            return (
                              <li key={opt.value}>
                                <label className={`explore-check ${checked ? 'is-checked' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleFilterValue(key, opt.value)}
                                  />
                                  <span className="explore-check-box" aria-hidden="true" />
                                  <span className="explore-check-label">{opt.label}</span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )
                    ) : null}
                  </section>
                );
              })}
            </div>
          </div>
        </aside>

        <div>
          <div className="explore-toolbar">
            <input
              type="text"
              className="text-input"
              style={{ width: '100%' }}
              placeholder="Search by title, description, or tag…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <p className="explore-showing">
              Showing {showingFrom}–{showingTo} of {filteredBooks.length}{' '}
              {filteredBooks.length === 1 ? 'book' : 'books'}
            </p>
          </div>

          <div className="explore-grid">
            {pagedBooks.length === 0 ? (
              <div className="empty-note" style={{ gridColumn: '1 / -1' }}>
                No books match selected filters.
              </div>
            ) : (
              pagedBooks.map((group) => {
                const main = group.digital || group.print;
                if (!main) return null;
                const bookUrl = group.url || main?.url || null;
                if (main && !main.url && bookUrl) main.url = bookUrl;

                const title = group.title || main.title || 'Untitled';
                const gradeLabel =
                  group.grade_name && group.grade_name !== 'N/A' ? group.grade_name : null;
                const pillChips = [];
                if (group.book_type_title && group.book_type_title !== 'N/A') {
                  pillChips.push(group.book_type_title);
                }
                if (group.subject_name && group.subject_name !== 'N/A') {
                  pillChips.push(group.subject_name);
                }
                if (group.country_name && group.country_name !== 'N/A') {
                  pillChips.push(group.country_name);
                }
                if (group.language_name && group.language_name !== 'N/A') {
                  pillChips.push(group.language_name);
                }
                if (group.version_label && group.version_label !== 'N/A') {
                  pillChips.push(group.version_label);
                }
                group.tags?.slice(0, 2).forEach((tag) => {
                  if (tag?.tag_name) pillChips.push(tag.tag_name);
                });

                return (
                  <article
                    key={`${group.book_id || group.title}-${group.isbn_code}-${group.grade_id}-${group.version_label}`}
                    className="explore-card"
                  >
                    <div className="explore-cover">
                      <PDFCoverPreview
                        pdfUrl={`/books/${main.book_id}/stream-cover`}
                        height={300}
                        bookTitle={title}
                      />
                    </div>
                    <div className="explore-body">
                      <h3 className="explore-title" title={title}>
                        {title}
                      </h3>
                      <div className="explore-tags">
                        {gradeLabel ? <span className="explore-meta-plain">{gradeLabel}</span> : null}
                        {pillChips.slice(0, 4).map((chip, i) => (
                          <span key={`${chip}-${i}`} className={`chip chip-${CHIP_TONES[i % CHIP_TONES.length]}`}>
                            {chip}
                          </span>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="explore-view-btn"
                        onClick={() => handleSelectBook(main)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        View
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {filteredBooks.length > 0 && (
            <div className="pagination explore-pagination">
              <span className="page-info">
                Showing {showingFrom} to {showingTo} of {filteredBooks.length} books
              </span>
              {totalPages > 1 && (
                <>
                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={safePage === 1}
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  {pageItems(safePage, totalPages).map((item, idx) =>
                    item === '…' ? (
                      <button key={`e-${idx}`} type="button" className="page-btn" disabled>
                        …
                      </button>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`page-btn${item === safePage ? ' active' : ''}`}
                        onClick={() => setCurrentPage(item)}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={safePage === totalPages}
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showIframe && iframeUrl && (
        <div
          className={`explore-viewer-overlay ${viewerFullscreen ? 'is-fullscreen' : ''}`}
          onClick={(e) => {
            if (e.target.classList.contains('explore-viewer-overlay')) closeIframe();
          }}
          role="dialog"
          aria-modal="true"
          aria-label={viewerTitle}
        >
          <div className="explore-viewer-modal">
            <div className="explore-viewer-header">
              <h2 className="explore-viewer-title" title={viewerTitle}>
                {viewerTitle}
              </h2>
              <div className="explore-viewer-actions">
                <button
                  type="button"
                  className="explore-viewer-fullscreen"
                  onClick={() => setViewerFullscreen((v) => !v)}
                  aria-label={viewerFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  title={viewerFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                  {viewerFullscreen ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                      <path d="M9 3H5v4M15 3h4v4M9 21H5v-4M15 21h4v-4" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                      <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  className="explore-viewer-close"
                  onClick={closeIframe}
                  aria-label="Close reader"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="explore-viewer-body">
              <iframe src={iframeUrl} className="explore-viewer-frame" title={viewerTitle} allowFullScreen />
            </div>
          </div>
        </div>
      )}

      {iframeLoading && (
        <div className="explore-viewer-overlay explore-iframe-loading">
          <div className="explore-viewer-loading-card">Opening book…</div>
        </div>
      )}

      {selectedBook && (
        <div
          className="bookx-details-overlay"
          onClick={handleOverlayClick}
          role="dialog"
          aria-modal="true"
        >
          <div className="bookx-details-container">
            <div className="bookx-details-header">
              <h3 className="section-heading">Book Specifications</h3>
            </div>
            <button
              type="button"
              className="bookx-close-button"
              onClick={() => setSelectedBook(null)}
              aria-label="Close details"
            >
              ×
            </button>

            <div className="bookx-details-content">
              <div className="bookx-table-cover-row">
                <div className="bookx-details-table-container">
                  <table className="bookx-details-table">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Subject</strong>
                        </td>
                        <td>{getName(subjects, selectedBook.subject_id, 'subject_id')}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Grade</strong>
                        </td>
                        <td>{getGradeName(selectedBook.grade_id)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Language</strong>
                        </td>
                        <td>{getName(languages, selectedBook.language_id, 'language_id')}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Type</strong>
                        </td>
                        <td>{selectedBook.book_type_title || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Country</strong>
                        </td>
                        <td>{selectedBook.country_name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>ISBN</strong>
                        </td>
                        <td>{selectedBook.isbn_code || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="bookx-details-image-wrapper">
                  <PDFCoverPreview
                    pdfUrl={`/books/${selectedBook.book_id}/stream-cover`}
                    width={280}
                    height={360}
                    bookTitle={selectedBook.title}
                  />
                </div>
              </div>
              <div className="bookx-details-meta">
              <h2 className="bookx-details-title">{selectedBook.title}</h2>
              <div
                className="bookx-details-description"
                style={{ whiteSpace: 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: truncatedDescription }}
              />
              {isLongDescription && (
                <button
                  type="button"
                  className="btn-quiet"
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                >
                  {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                </button>
              )}

              <div className="bookx-details-tags">
                <strong>Tags: </strong>
                {selectedBook.tags?.length > 0
                  ? selectedBook.tags.map((tag) => (
                      <span key={tag.tag_id} className="chip chip-lilac">
                        {tag.tag_name}
                      </span>
                    ))
                  : 'None'}
              </div>
              </div>
            </div>

              <div className="btn-row bookx-details-buttons">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedBook(null)}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleViewBook}
                  disabled={iframeLoading}
                >
                  {iframeLoading ? 'Opening…' : 'View Book'}
                </button>
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default Books;
